/**
 * Cart state.
 *
 * Persisted to IndexedDB, not memory. A retailer building a twenty-line order
 * in a shop will get a phone call, switch apps, or lose signal — losing the
 * cart to any of those is the fastest way to make them go back to WhatsApp
 * orders.
 *
 * The cart stores product ids and quantities only. **Prices are never computed
 * here.** Totals come from the server so the figure the customer sees is the
 * figure that lands on the invoice, computed by the same code path.
 */

import {
  createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode,
} from 'react';
import * as idb from './idb';

export interface CartLine {
  productId: string;
  skuCode: string;
  productName: string;
  uom: string;
  quantity: number;
  /**
   * The pack, carried so the cart can state a quantity in boxes.
   *
   * Display only. The QUANTITY is always in base units and the server is never
   * told otherwise — the cart holding one meaning for its numbers is what keeps
   * a box from reaching an invoice as a bottle.
   *
   * Optional because a cart saved before this existed will not have it, and an
   * old cart must still check out.
   */
  packUom?: string | null;
  packQty?: number | null;
  /**
   * THE UNIT THIS LINE IS ORDERED IN.
   *
   * The cart used to be pieces-only, and the comment where the box figure was
   * displayed argued for that: a unit TOGGLE would make the same "2" on screen
   * mean two bottles or forty-eight depending on a setting nobody noticed, and
   * on this project that ambiguity has a name — a 24x dispute, settled at the
   * retailer's door.
   *
   * That reasoning still holds and this is not a toggle. Boxes and pieces are
   * TWO SEPARATE LINES, each labelled with its unit, each with its own price,
   * shown side by side. Nothing changes meaning under anybody; a product
   * ordered both ways appears twice, which is what the client asked for on
   * 2 Oct and is what the shopkeeper actually says: "five cases and seven
   * loose".
   *
   * Optional because a cart saved before this existed has no unit, and an old
   * cart must still check out — absent means pieces.
   */
  soldUom?: 'PCS' | 'BOX';
  /** Catalogue price of one box, when the catalogue has one. Display only. */
  boxPricePaise?: number | null;
  /** Snapshot for display only — the server re-prices on submit. */
  ratePaise: number;
  imageUrl: string | null;
  /**
   * A selling price typed by staff, in paise.
   *
   * Unlike `ratePaise` this is NOT a display snapshot: it is sent to the
   * server and used. Undefined means "use the catalogue", which is what a
   * customer's own cart always means — the server ignores this field entirely
   * for a customer, so the two do not have to trust each other.
   */
  typedPricePaise?: number;
}

/**
 * The identity of a basket line: a product AND the unit it is being bought in.
 *
 * Exported because every caller that used to pass a product id must now pass
 * this, and a silent mismatch would be invisible — the wrong line would simply
 * be updated.
 */
export function lineKey(productId: string, soldUom: 'PCS' | 'BOX' = 'PCS'): string {
  return `${productId}:${soldUom}`;
}

/** The key of an existing line. Absent unit means pieces, for old saved carts. */
export function keyOf(line: CartLine): string {
  return lineKey(line.productId, line.soldUom ?? 'PCS');
}

/**
 * The catalogue rate for the unit a line is bought in.
 *
 * A box with no box price falls back to the piece price times the pack — the
 * same derivation the server makes, kept identical here so the estimate on the
 * basket button does not disagree with the invoice that follows.
 */
export function rateOf(line: CartLine): number {
  if ((line.soldUom ?? 'PCS') !== 'BOX') return line.ratePaise;
  if (line.boxPricePaise && line.boxPricePaise > 0) return line.boxPricePaise;
  return line.ratePaise * (line.packQty ?? 1);
}

interface CartState {
  shopId: string | null;
  /**
   * Who the order is FOR.
   *
   * Null when a customer is ordering for themselves — the server takes the
   * customer from the token in that case, and it must, because a customer may
   * never order for anyone else.
   *
   * Set when a salesperson, driver or admin is ordering ON BEHALF of a
   * retailer. This is the piece that was missing: `Catalogue` and `Cart` both
   * read `user.customerId`, which is null for every staff role, so the
   * catalogue loaded unpriced, no shops were fetched, and the place button
   * could never enable. The salesperson's "Book Order" button navigated with
   * the customer in router state and nothing read it — and router state is
   * lost on reload anyway, which is exactly when a phone in a shop reloads.
   */
  customerId: string | null;
  customerName: string | null;
  lines: CartLine[];
}

interface CartValue {
  lines: CartLine[];
  shopId: string | null;
  /** The customer this order is for, when ordering on someone's behalf. */
  customerId: string | null;
  customerName: string | null;
  count: number;
  totalQuantity: number;
  /** Indicative only. The authoritative total comes from the order response. */
  estimatedGrossPaise: number;

  setShop: (shopId: string) => void;
  /** Begin (or switch) an on-behalf order. Switching customers clears the cart. */
  setCustomer: (customerId: string, customerName: string) => void;
  /** Abandon the on-behalf order entirely. */
  clearCustomer: () => void;
  add: (line: Omit<CartLine, 'quantity'>, quantity?: number) => void;
  /**
   * Every line operation is keyed by `lineKey`, NOT by product id.
   *
   * One product can now be in the basket twice — as boxes and as loose pieces,
   * at two prices. Keying by product id would make "set the quantity of
   * Campa 250ml" ambiguous, and the implementation would quietly pick the
   * first match: typing a piece quantity would overwrite the box line.
   */
  setQuantity: (key: string, quantity: number) => void;
  /** Staff only in practice; the server ignores it for a customer. */
  setPrice: (key: string, paise: number | null) => void;
  remove: (key: string) => void;
  clear: () => void;
  quantityOf: (productId: string, soldUom?: 'PCS' | 'BOX') => number;
  /** Replace the whole cart, used by "repeat last order". */
  replaceAll: (lines: CartLine[]) => void;
}

const CartContext = createContext<CartValue>({
  lines: [], shopId: null, customerId: null, customerName: null,
  count: 0, totalQuantity: 0, estimatedGrossPaise: 0,
  setShop: () => {}, setCustomer: () => {}, clearCustomer: () => {},
  add: () => {}, setQuantity: () => {}, setPrice: () => {}, remove: () => {},
  clear: () => {}, quantityOf: () => 0, replaceAll: () => {},
});

const KEY = 'cart';

export function CartProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<CartState>({
    shopId: null, customerId: null, customerName: null, lines: [],
  });
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    void (async () => {
      const stored = await idb.kvGet<CartState>(KEY);
      if (stored) setState(stored);
      setLoaded(true);
    })();
  }, []);

  // Persist on every change. Cheap, and it means a hard app kill loses nothing.
  useEffect(() => {
    if (!loaded) return;
    void idb.kvSet(KEY, state);
  }, [state, loaded]);

  const setShop = useCallback((shopId: string) => {
    setState((prev) => {
      // Switching shop clears the cart. A multi-shop owner ordering for one
      // outlet must not silently carry lines over to another — the delivery
      // address and the invoice would both be wrong.
      if (prev.shopId && prev.shopId !== shopId) return { ...prev, shopId, lines: [] };
      return { ...prev, shopId };
    });
  }, []);

  const setCustomer = useCallback((customerId: string, customerName: string) => {
    setState((prev) => {
      // Switching customer discards everything, shop included. A salesperson
      // moving to the next shop on the beat must not carry the previous
      // retailer's lines with them — that would bill the wrong person for
      // goods they never ordered, and it would look like a working order.
      if (prev.customerId && prev.customerId !== customerId) {
        return { customerId, customerName, shopId: null, lines: [] };
      }
      return { ...prev, customerId, customerName };
    });
  }, []);

  const clearCustomer = useCallback(() => {
    setState({ customerId: null, customerName: null, shopId: null, lines: [] });
  }, []);

  const add = useCallback((line: Omit<CartLine, 'quantity'>, quantity = 1) => {
    setState((prev) => {
      const key = lineKey(line.productId, line.soldUom);
      const existing = prev.lines.find((l) => keyOf(l) === key);
      if (existing) {
        return {
          ...prev,
          lines: prev.lines.map((l) =>
            keyOf(l) === key ? { ...l, quantity: l.quantity + quantity } : l),
        };
      }
      return { ...prev, lines: [...prev.lines, { ...line, quantity }] };
    });
  }, []);

  const setQuantity = useCallback((key: string, quantity: number) => {
    setState((prev) => ({
      ...prev,
      // Zero removes the line. That is what makes "both fields start at 0"
      // work: a box stepper taken back down to zero leaves no empty box line
      // behind to be priced, sent and invoiced for nothing.
      lines: quantity <= 0
        ? prev.lines.filter((l) => keyOf(l) !== key)
        : prev.lines.map((l) => (keyOf(l) === key ? { ...l, quantity } : l)),
    }));
  }, []);

  /**
   * Set or clear the typed selling price on a line.
   *
   * `null` clears it and the line goes back to the catalogue price. That is a
   * separate act from typing zero, which the server refuses — a line at ₹0
   * invoices the shop for nothing and looks entirely normal on every screen.
   */
  const setPrice = useCallback((key: string, paise: number | null) => {
    setState((prev) => ({
      ...prev,
      lines: prev.lines.map((l) => (keyOf(l) === key
        ? { ...l, typedPricePaise: paise ?? undefined }
        : l)),
    }));
  }, []);

  const remove = useCallback((key: string) => {
    setState((prev) => ({ ...prev, lines: prev.lines.filter((l) => keyOf(l) !== key) }));
  }, []);

  const clear = useCallback(() => {
    // Keeps the shop and the on-behalf customer: "empty the basket", not
    // "start over". Use `clearCustomer` to end an on-behalf order.
    setState((prev) => ({ ...prev, lines: [] }));
  }, []);

  const replaceAll = useCallback((lines: CartLine[]) => {
    setState((prev) => ({ ...prev, lines }));
  }, []);

  const value = useMemo<CartValue>(() => ({
    lines: state.lines,
    shopId: state.shopId,
    customerId: state.customerId,
    customerName: state.customerName,
    count: state.lines.length,
    totalQuantity: state.lines.reduce((s, l) => s + l.quantity, 0),
    // The typed price where there is one, so the estimate on the basket
    // button matches what the person just entered rather than the catalogue
    // figure they deliberately replaced.
    estimatedGrossPaise: state.lines.reduce(
      // `rateOf` picks the box rate for a box line. Using `ratePaise` for both
      // would under-estimate a basket of cartons by the pack factor, and the
      // figure sits on the button somebody taps to commit.
      (s, l) => s + l.quantity * (l.typedPricePaise ?? rateOf(l)), 0,
    ),
    setShop, setCustomer, clearCustomer, add, setQuantity, setPrice, remove, clear, replaceAll,
    quantityOf: (productId: string, soldUom: 'PCS' | 'BOX' = 'PCS') =>
      state.lines.find((l) => keyOf(l) === lineKey(productId, soldUom))?.quantity ?? 0,
  }), [state, setShop, setCustomer, clearCustomer, add, setQuantity, setPrice, remove, clear, replaceAll]);

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart(): CartValue {
  return useContext(CartContext);
}
